<template>
  <v-container fluid class="fill-height">
    <v-container v-if="loading">
      <v-row>
        <v-col>
          <v-progress-circular indeterminate />
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else-if="training" fluid class="fill-height align-start">
      <v-overlay v-if="isSaving">
        <v-progress-circular indeterminate size="64" />
      </v-overlay>

      <v-row>
        <!--course left column-->
        <v-col cols="12" md="6">
          <!--course save, submit, approve, reject buttons done-->
          <v-row v-if="$vuetify.breakpoint.smAndDown" no-gutters justify="end">
            <v-col v-if="$store.getters.isLocaleEnabled" cols="auto">
              <v-btn
                depressed
                min-width="40"
                color="primaryDark"
                class="white--text"
                @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'"
              >
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <!--course save, submit buttons done-->
              <CourseSaveAndSubmit
                :training="training"
                :isSaving.sync="isSaving"
                :isEditing.sync="isEditing"
                @onCancel="resetTraining"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>

            <v-col cols="auto">
              <!--course approve, reject buttons done-->
              <CourseApproveOrReject
                :training="training"
                :isEditing="isEditing"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>
          </v-row>

          <!--course title done-->
          <CourseTitle
            :training.sync="training"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
          />

          <v-row justify="space-between" align="center" class="mb-2">
            <v-col class="text-start">
              <CourseCategory
                :training.sync="training"
                :isEditing="isEditing"
              />
            </v-col>

            <!--course price done-->
            <v-col v-if="!isEditing" class="text-end">
              <CoursePrice :training.sync="training" :isEditing="isEditing" />
            </v-col>
          </v-row>

          <!--course duration done-->
          <CourseDuration :training.sync="training" :isEditing="isEditing" />

          <!--course validity done-->
          <CourseValidity :training.sync="training" :isEditing="isEditing" />

          <!--course price done-->
          <CoursePrice
            v-if="isEditing"
            :training.sync="training"
            :isEditing="isEditing"
          />

          <!--course tabs done-->
          <div class="mb-6 text-start">
            <v-row
              v-for="tab in tabs"
              :key="'tab_' + tab"
              no-gutters
              class="mb-3"
            >
              <v-col>
                <span
                  class="subtitle-1 font-weight-medium hand-cursor"
                  :class="
                    tab === 'feedback' && hasInvalidFeedback
                      ? 'error--text'
                      : selectedTab === tab
                      ? 'primary--text'
                      : 'textLight--text'
                  "
                  @click="selectedTab = tab"
                >
                  {{ $t(`course.courseDetails.${tab}`) }}
                </span>

                <v-divider
                  class="my-2"
                  style="width: 100px"
                  :class="selectedTab === tab ? 'primary' : 'textLight'"
                />
              </v-col>
            </v-row>
          </div>

          <!--course syllabus done-->
          <CourseSyllabus
            :training.sync="training"
            :isEditing="isEditing && selectedTab === 'syllabus'"
            :selectedLocale="selectedLocale"
            class="mb-md-6"
            @onTrainingModuleContentSelection="onTrainingModuleContentSelection"
            @onSuccess="snackbarSuccess = true"
            @onError="snackbarError = true"
          />
        </v-col>

        <!--course right column-->
        <v-col cols="12" md="6">
          <!--course save, submit, approve, reject buttons done-->
          <v-row v-if="$vuetify.breakpoint.mdAndUp" no-gutters justify="end">
            <v-col
              v-if="$store.getters.isLocaleEnabled"
              cols="auto"
              class="pb-6"
            >
              <v-btn
                depressed
                color="primaryDark"
                class="white--text"
                @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'"
              >
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <!--course save, submit buttons done-->
              <CourseSaveAndSubmit
                :training="training"
                :isSaving.sync="isSaving"
                :isEditing.sync="isEditing"
                @onCancel="resetTraining"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>

            <v-col cols="auto">
              <!--course approve, reject buttons done-->
              <CourseApproveOrReject
                :training="training"
                :isEditing="isEditing"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>
          </v-row>

          <CourseImage
            :training.sync="training"
            :isEditing="isEditing"
            class="mb-6"
            @onSuccess="snackbarSuccess = true"
            @onError="snackbarError = true"
          />

          <!--course content anchor-->
          <div id="courseModuleContent"></div>

          <CourseAbout
            v-if="selectedTab === 'about'"
            :training.sync="training"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
            @onSuccess="snackbarSuccess = true"
            @onError="snackbarError = true"
          />

          <CourseInstructions
            v-else-if="selectedTab === 'instructions'"
            :training.sync="training"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
            @onSuccess="snackbarSuccess = true"
            @onError="snackbarError = true"
          />

          <CourseFeedbackQuiz
            v-else-if="selectedTab === 'feedback'"
            :training.sync="training"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
            @onSuccess="snackbarSuccess = true"
            @onError="snackbarError = true"
          />

          <template
            v-else-if="
              selectedTab === 'syllabus' && selectedTrainingModuleContent
            "
          >
            <CourseContentVideo
              v-if="canShowContent('VIDEO')"
              :key="courseContentRenderKey"
              :training.sync="training"
              :selectedTrainingModuleContent.sync="
                selectedTrainingModuleContent
              "
              :isEditing="isEditing"
              :selectedLocale="selectedLocale"
              class="mb-6"
              @onSuccess="snackbarSuccess = true"
              @onError="snackbarError = true"
            />

            <CourseContentAudio
              v-else-if="canShowContent('AUDIO')"
              :key="courseContentRenderKey"
              :training.sync="training"
              :selectedTrainingModuleContent.sync="
                selectedTrainingModuleContent
              "
              :isEditing="isEditing"
              :selectedLocale="selectedLocale"
              class="mb-6"
              @onSuccess="snackbarSuccess = true"
              @onError="snackbarError = true"
            />

            <CourseContentText
              v-else-if="canShowContent('TEXT')"
              :key="courseContentRenderKey"
              :training.sync="training"
              :selectedTrainingModuleContent.sync="
                selectedTrainingModuleContent
              "
              :isEditing="isEditing"
              :selectedLocale="selectedLocale"
              class="mb-6"
              @onSuccess="snackbarSuccess = true"
              @onError="snackbarError = true"
            />

            <CourseContentQuiz
              v-else-if="canShowContent('QUIZ')"
              :key="courseContentRenderKey"
              :training.sync="training"
              :selectedTrainingModuleContent.sync="
                selectedTrainingModuleContent
              "
              :isEditing="isEditing"
              :selectedLocale="selectedLocale"
              class="mb-6"
              @onSuccess="snackbarSuccess = true"
              @onError="snackbarError = true"
            />
          </template>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else>
      <Error />
    </v-container>

    <v-snackbar v-model="snackbarSuccess" timeout="3000" color="success">
      {{
        snackbarSuccessMessage || $t("course.courseDetails.successfullySave")
      }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="snackbarSuccess = false"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="snackbarError" timeout="3000" color="error">
      {{ snackbarErrorMessage || $t("somethingWentWrong") }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="snackbarError = false"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import CourseTitle from "../components/course-details/CourseTitle";
import CourseDuration from "@/app/course-module/components/course-details/CourseDuration";
import CourseValidity from "@/app/course-module/components/course-details/CourseValidity";
import CourseSyllabus from "../components/course-details/CourseSyllabus";
import CourseSaveAndSubmit from "@/app/course-module/components/course-details/CourseSaveAndSubmit";
import CourseApproveOrReject from "../components/course-details/CourseApproveOrReject";
import CourseImage from "../components/course-details/CourseImage";
import CourseAbout from "../components/course-details/CourseAbout";
import CourseInstructions from "../components/course-details/CourseInstructions";
import CourseContentVideo from "../components/course-details/CourseContentVideo";
import CourseContentAudio from "../components/course-details/CourseContentAudio";
import CourseContentText from "../components/course-details/CourseContentText";
import CourseContentQuiz from "../components/course-details/CourseContentQuiz";
import Error from "../../core-module/components/Error";
import CoursePrice from "@/app/course-module/components/course-details/CoursePrice";
import CourseCategory from "@/app/course-module/components/course-details/CourseCategory";
import CourseFeedbackQuiz from "../components/course-details/CourseFeedbackQuiz.vue";

export default {
  name: "CourseDetailsPage",
  components: {
    CourseCategory,
    CoursePrice,
    CourseTitle,
    CourseDuration,
    CourseValidity,
    CourseSyllabus,
    CourseSaveAndSubmit,
    CourseApproveOrReject,
    CourseImage,
    CourseAbout,
    CourseInstructions,
    CourseContentVideo,
    CourseContentAudio,
    CourseContentText,
    CourseContentQuiz,
    CourseFeedbackQuiz,
    Error,
  },
  data() {
    return {
      snackbarSuccess: false,
      snackbarError: false,
      snackbarSuccessMessage: null,
      snackbarErrorMessage: null,
      loading: true,
      trainingOg: null,
      training: null,
      isSaving: false,
      isEditing: false,
      selectedLocale: "en",
      tabs: ["about", "instructions", "feedback", "syllabus"],
      selectedTab: "",
      selectedTrainingModuleContent: null,
    };
  },
  computed: {
    hasInvalidFeedback() {
      return this.training.feedback?.questionAnswers?.some(
        fq => fq.hasInvalidQuestion || fq.hasInvalidChoices
      );
    },
    courseContentRenderKey() {
      const trainingModuleIndex =
        this.selectedTrainingModuleContent?.trainingModuleIndex;
      const trainingModuleContentIndex =
        this.selectedTrainingModuleContent?.trainingModuleContentIndex;
      const contentType =
        this.training.trainingModules[trainingModuleIndex]
          ?.trainingModuleContents[trainingModuleContentIndex]?.contentType;

      return `${contentType}_${trainingModuleIndex}_${trainingModuleContentIndex}`;
    },
  },
  watch: {
    "$route.params.trainingId": {
      handler() {
        this.fetchTraining();
      },
      immediate: true,
    },
  },
  methods: {
    fetchTraining() {
      this.loading = true;
      this.trainingOg = null;
      this.training = null;
      this.selectedTab = "";
      this.selectedTrainingModuleContent = null;

      if (this.$route.params.trainingId) {
        this.$apollo.addSmartQuery("getTraining", {
          query: require("../api/query/getTraining.gql"),
          variables: { id: this.$route.params.trainingId },
          result({ data, loading }) {
            if (data && data.getTraining) {
              this.trainingOg = this.sortTrainingModulesInTraining(
                data.getTraining
              );
              //TODO: find a way to prevent pending changes not reset on individual save
              this.training = this.deepCloneObject(this.trainingOg);
              this.training = this.validateTraining(this.training);

              if (this.selectedTab === "") this.selectedTab = "about";
            }

            this.loading = loading;
          },
        });
      } else {
        this.trainingOg = {
          // trainingCategories: [],
          // title: [],
          // description: [],
          // instructions: [],
          // images: [],
          // certificateValidity: null,
          isActive: true,
          hasInvalidTitle: true,
          // trainingModules: [],
        };

        this.training = this.deepCloneObject(this.trainingOg);

        this.isEditing = true;
        this.loading = false;
      }
    },
    resetTraining() {
      this.training = this.deepCloneObject(this.trainingOg);
      this.training = this.validateTraining(this.training);
      this.selectedTrainingModuleContent = null;
    },
    canShowContent(contentType) {
      return (
        this.training.trainingModules[
          this.selectedTrainingModuleContent?.trainingModuleIndex
        ]?.trainingModuleContents[
          this.selectedTrainingModuleContent?.trainingModuleContentIndex
        ]?.contentType === contentType
      );
    },
    onTrainingModuleContentSelection(trainingModuleContent) {
      this.selectedTab = "syllabus";
      this.selectedTrainingModuleContent = trainingModuleContent;

      if (
        this.selectedTrainingModuleContent &&
        this.$vuetify.breakpoint.mdAndDown
      ) {
        setTimeout(() => {
          const el = document.getElementById("courseModuleContent");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    },
  },
};
</script>

<style lang="scss" scoped />
